import {
  CONTINUATION_CHROME_HEIGHT_PX,
  getContentWidthPx,
  getUsableContentHeightPx,
  LETTER_LETTERHEAD_TEMPLATE,
  LINE_HEIGHT_PX,
  TABLE_HEADER_HEIGHT_PX,
  type PageTemplate,
} from './page-template';
import { estimateWrappedLineCount } from './description-chunks';
import type { MeasuredBlocks, PrintDocumentModel, QuotationPrintModel } from './types';
import { quotationToPrintDocument } from './adapters/quotation-adapter';

export type MeasureOptions = {
  template?: PageTemplate;
  contentWidthPx?: number;
};

function charsPerLineForWidth(contentWidthPx: number, avgCharPx = 6.2): number {
  const descWidth = contentWidthPx * 0.42;
  return Math.max(20, Math.floor(descWidth / avgCharPx));
}

function textHeight(text: string, charsPerLine: number, lineHeight = LINE_HEIGHT_PX): number {
  return estimateWrappedLineCount(text, charsPerLine) * lineHeight;
}

/**
 * Measure a generic PrintDocumentModel for the page planner.
 */
export function measurePrintBlocks(
  model: PrintDocumentModel,
  options: MeasureOptions = {}
): MeasuredBlocks {
  const template = options.template ?? LETTER_LETTERHEAD_TEMPLATE;
  const contentWidthPx = options.contentWidthPx ?? getContentWidthPx(template);
  const cpl = charsPerLineForWidth(contentWidthPx);

  const firstSections: Record<string, number> = {};
  for (const s of model.firstPageSections) {
    firstSections[s.id] = s.heightPx;
  }

  const finalSections: Record<string, number> = {};
  for (const s of model.finalSections) {
    finalSections[s.id] = s.heightPx;
  }

  const itemFull: number[][] = [];
  const itemChunks: number[][][] = [];
  const itemRowChrome: number[][] = [];

  for (const table of model.tables) {
    const fulls: number[] = [];
    const chunksArr: number[][] = [];
    const chromes: number[] = [];
    for (const row of table.rows) {
      const extra = (row.extraLines || []).reduce((sum, line) => sum + textHeight(line, cpl), 0);
      const chrome = 22 + extra;
      const chunks =
        row.descriptionChunks.length > 0 ? row.descriptionChunks : [row.description || ''];
      const chunkHeights = chunks.map((c) => Math.max(LINE_HEIGHT_PX, textHeight(c, cpl)));
      const descTotal = chunkHeights.reduce((s, h) => s + h, 0);
      chunksArr.push(chunkHeights);
      chromes.push(chrome);
      fulls.push(chrome + descTotal);
    }
    itemFull.push(fulls);
    itemChunks.push(chunksArr);
    itemRowChrome.push(chromes);
  }

  return {
    firstSections,
    finalSections,
    continuationChrome: CONTINUATION_CHROME_HEIGHT_PX,
    tableHeader: TABLE_HEADER_HEIGHT_PX,
    itemFull,
    itemChunks,
    itemRowChrome,
    pageNumber: 18,
  };
}

/** @deprecated Use measurePrintBlocks via quotation adapter */
export function measureQuotationBlocks(
  model: QuotationPrintModel,
  options: MeasureOptions = {}
): MeasuredBlocks {
  return measurePrintBlocks(quotationToPrintDocument(model), options);
}

export function getPlannerUsableHeightPx(
  template: PageTemplate = LETTER_LETTERHEAD_TEMPLATE,
  opts?: { reserveChromeBands?: boolean }
): number {
  return getUsableContentHeightPx(template, 96, opts);
}
